import { name } from "./__init__";
import { createSelector } from "reselect";
import constants, { BOOKING_FORM_NAME_MOUNTPOINT } from "../../utils/constants";
import range from "lodash/range";
import { calculateSelectorsRange } from "../../utils/bookingFormUtils";
import { restoredSessionSelector } from "../App/selectors";
import get from "lodash/get";

export const getModelSelector = state => state[name];

/* TODO: this is almost a hack to avoid circular dependencies  */
export const getAppModelSelector = state => state.app;

export const configurationSelector = createSelector(
  getAppModelSelector,
  model => model && model.get(constants.CONFIGURATION_KEY)
);

export const bookingFormSelector = state => state[BOOKING_FORM_NAME_MOUNTPOINT];

export const guestAgesSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("guestAges")
);

export const childrenCountSelector = createSelector(
  bookingFormSelector,
  guestAgesSelector,
  (formData, guestAges) =>
    (formData && formData.childrenCount && formData.childrenCount.value) ||
    (guestAges && guestAges.size) ||
    null
);

/* Created only to reduce computations when configuration changes */
export const bookingFormFieldsSelectorInput = createSelector(
  configurationSelector,
  configuration => {
    if (!configuration) {
      return null;
    }
    return {
      occupantsMaxValues: configuration.get("occupantsMaxValues"),
      occupantsMinAge: configuration.get("occupantsMinAge"),
      occupantsFilterType: configuration.get("occupantsFilterType"),
      showHotelSelector: configuration.get("showHotelSelector"),
      ageRanges: configuration.get("ageRanges"),
    };
  }
);

export const showChildrenCounter = (occupantsMaxValues, occupantsFilterType) =>
  occupantsFilterType === constants.OCCUPANTS_FILTER_TYPE.AGE &&
  (occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER) ||
    occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN) ||
    occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY));

export const hasFiltersSelector = createSelector(getAppModelSelector, model => {
  const propertyTicker = model.get("propertyTicker");
  const propertyData =
    propertyTicker && model.get("propertyData") && model.get("propertyData").get(propertyTicker);

  return propertyData && propertyData.get("filters") && propertyData.get("filters").size > 0;
});

export const occupantsSelectorRangeSelector = createSelector(
  configurationSelector,
  configuration =>
    configuration && {
      [constants.OCCUPANTS_TYPE.SENIOR]: configuration.get("seniorSelectorRange"),
      [constants.OCCUPANTS_TYPE.ADULT]: configuration.get("adultSelectorRange"),
      [constants.OCCUPANTS_TYPE.TEENAGER]: configuration.get("teenagerSelectorRange"),
      [constants.OCCUPANTS_TYPE.CHILDREN]: configuration.get("childSelectorRange"),
      [constants.OCCUPANTS_TYPE.BABY]: configuration.get("babySelectorRange"),
    }
);

export const bookingFormFieldsSelector = createSelector(
  bookingFormFieldsSelectorInput,
  childrenCountSelector,
  configurationSelector,
  hasFiltersSelector,
  occupantsSelectorRangeSelector,
  (bookingFormFieldsInput, childrenCount, configuration, hasFilters, occupantsSelectorRange) => {
    if (!bookingFormFieldsInput) {
      return null;
    }
    const {
      occupantsMaxValues,
      occupantsFilterType,
      occupantsMinAge,
      showHotelSelector,
    } = bookingFormFieldsInput;
    const showChildrenCount = showChildrenCounter(occupantsMaxValues, occupantsFilterType);

    const childrenFields = childrenCount
      ? range(childrenCount).reduce((previous, idx) => ({ ...previous, [`age${idx}`]: true }), {})
      : [];

    const fields = {
      startDate: true,
      endDate: true,
      childrenCount: showChildrenCount,
      hotelSelector: showHotelSelector,
      promoCodes: true,
      filters: hasFilters,
      ...childrenFields,
    };

    switch (configuration.get("occupantRestriction")) {
      case constants.OCCUPANT_RESTRICTION.TAKE_ALL_PEOPLE:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_CHILD:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_TEENAGER_CHILD:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_TEENAGER_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_SENIOR_ADULT_TEENAGER_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.SENIOR] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.SENIOR
        );
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.NONE:
      default:
        break;
    }

    const selectorsRange = calculateSelectorsRange({
      showChildrenCount,
      occupantsMinAge,
      occupantsMaxValues,
      occupantsSelectorRange,
      fields,
    });
    const ageRanges = bookingFormFieldsInput.ageRanges && bookingFormFieldsInput.ageRanges.toJS();
    return { fields, selectorsRange, ageRanges };
  }
);

/* Created only to reduce computations when configuration changes */
export const bookingFormInitialValuesSelectorInput = createSelector(
  configurationSelector,
  configuration => {
    return (
      configuration && {
        ...constants.OCCUPANTS_TYPE_ORDER.reduce(
          (previous, type) => ({
            ...previous,
            [type]: configuration.get(type),
          }),
          {}
        ),
        guestAges: configuration.get("guestAges"),
        startDate: configuration.get("startDate"),
        endDate: configuration.get("endDate"),
        promoCodes: configuration.get("promoCodes"),
        filters: configuration.get("filters"),
      }
    );
  }
);

export const bookingFormInitialValuesSelector = createSelector(
  restoredSessionSelector,
  bookingFormInitialValuesSelectorInput,
  (restoredSession, configurationSubset) => {
    if (!configurationSubset) {
      return null;
    }
    const { guestAges, ...rest } = configurationSubset;

    let initialValues = rest;

    if (guestAges && guestAges.size > 0) {
      initialValues = {
        ...initialValues,
        ...guestAges.reduce((previous, age, idx) => ({ ...previous, [`age${idx}`]: age }), {}),
        childrenCount: guestAges.size,
        guestAges: guestAges.toJS(),
      };
    }

    if (restoredSession) {
      const startDate = get(restoredSession, "ariRequest.start");
      if (startDate) initialValues.startDate = new Date(startDate);
      const endDate = get(restoredSession, "ariRequest.end");
      if (endDate) initialValues.endDate = new Date(endDate);
    }

    return initialValues;
  }
);
